// src/pages/CustomerReport.jsx
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Table, Button, Container, Row, Col, Form } from 'react-bootstrap';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const CustomerReport = () => {
  const [report, setReport] = useState([]);
  const [search, setSearch] = useState('');
  const [filtered, setFiltered] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [summary, setSummary] = useState({ total_customers: 0, total_paid: 0, total_unpaid: 0 });
  const reportRef = useRef();

  const fetchReport = () => {
    const params = { start: startDate, end: endDate };
    const API_BASE = process.env.REACT_APP_API_BASE_URL;
  

    axios.get(`${API_BASE}/api/reports/customers1`, {
      params,
      headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
    })
      .then(res => {
        setReport(res.data);
        setFiltered(res.data);
        setCurrentPage(1);

        const totalPaid = res.data.reduce((sum, c) => sum + (c.total_paid || 0), 0);
        const totalUnpaid = res.data.reduce((sum, c) => sum + (c.total_unpaid || 0), 0);
        const totalCustomers = res.data.length;
        setSummary({ total_paid: totalPaid, total_unpaid: totalUnpaid, total_customers: totalCustomers });
      })
      .catch(err => console.error(err));
  };

  useEffect(() => {
    fetchReport();
  }, []);

  useEffect(() => {
    const filteredData = report.filter(c =>
      c.company_name.toLowerCase().includes(search.toLowerCase())
    );
    setFiltered(filteredData);
    setCurrentPage(1);
  }, [search, report]);

  const exportPDF = () => {
    const input = reportRef.current;
    html2canvas(input).then(canvas => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(18);
      pdf.text('SAL PITA - Customer Report', pdfWidth / 2, 20, { align: 'center' });
      pdf.setFontSize(12);
      pdf.addImage(imgData, 'PNG', 10, 30, pdfWidth - 20, pdfHeight);
      pdf.save('customer_report.pdf');
    });
  };

  const startIdx = (currentPage - 1) * rowsPerPage;
  const endIdx = rowsPerPage === 'all' ? filtered.length : startIdx + Number(rowsPerPage);
  const paginatedData = rowsPerPage === 'all' ? filtered : filtered.slice(startIdx, endIdx);
  const totalPages = rowsPerPage === 'all' ? 1 : Math.ceil(filtered.length / rowsPerPage);

  return (
    <Container>
      <h3 className="mb-4">Customer Report</h3>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Control type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </Col>
        <Col md={3}>
          <Form.Control type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </Col>
        <Col md={2}>
          <Button variant="success" onClick={fetchReport}>Filter</Button>
        </Col>
        <Col md={4} className="text-end">
          <Button variant="secondary" onClick={exportPDF}>Export PDF</Button>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Search by company name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </Col>
        <Col md={2}>
          <Form.Select value={rowsPerPage} onChange={e => setRowsPerPage(e.target.value)}>
            <option value={10}>10 rows</option>
            <option value={20}>20 rows</option>
            <option value={30}>30 rows</option>
            <option value="all">Show All</option>
          </Form.Select>
        </Col>
      </Row>

      <div ref={reportRef} style={{ padding: '20px', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '10px' }}>
        <h5>Total Customers: {summary.total_customers}</h5>
        <h5>Total Paid: ${summary.total_paid.toFixed(2)}</h5>
        <h5>Total Unpaid: ${summary.total_unpaid.toFixed(2)}</h5>

        <Table striped bordered hover>
          <thead className="table-dark">
            <tr>
              <th>Customer ID</th>
              <th>Company Name</th>
              <th>Total Orders</th>
              <th>Total Paid</th>
              <th>Total Unpaid</th>
              <th>Last Order</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((r, idx) => (
              <tr key={idx}>
                <td>{r.customer_id}</td>
                <td>{r.company_name}</td>
                <td>{r.total_orders}</td>
                <td>${r.total_paid?.toFixed(2) || '0.00'}</td>
                <td>${r.total_unpaid?.toFixed(2) || '0.00'}</td>
                <td>{r.last_order ? new Date(r.last_order).toLocaleDateString() : 'N/A'}</td>
              </tr>
            ))}
          </tbody>
        </Table>

        {rowsPerPage !== 'all' && totalPages > 1 && (
          <div className="d-flex justify-content-center mt-4">
            {[...Array(totalPages)].map((_, i) => (
              <Button
                key={i}
                variant={i + 1 === currentPage ? 'primary' : 'outline-secondary'}
                className="mx-1"
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </Button>
            ))}
          </div>
        )}
      </div>
    </Container>
  );
};

export default CustomerReport;
